/**
 * What this person drew in this session, and what they did with it (owner, 2026-09-17).
 *
 * **The eraser, Clear and Undo reach only this.** The owner's call: a person takes back what
 * they drew *now*, not what anybody — themselves included — drew last week. The server knows
 * authors, not sessions (`DELETE /markups/{id}` answers `403` to anybody but the author), so the
 * narrower rule lives here, on the client, and nowhere else.
 *
 * **A session is this tab, until it reloads.** Module state rather than `sessionStorage`:
 * leaving a drawing and coming back keeps what you drew erasable, a reload is a fresh start.
 * Nothing about it is fetched or shared — it is a list of ids this tab has been told by its own
 * writes.
 *
 * **History is per version.** Undo on r04 must not take back a stroke drawn on r03 a minute
 * ago, which is on a sheet nobody is looking at. Each entry is one act: one stroke drawn, or one
 * erase (a Clear is one erase of many strokes, so one Undo brings them all back).
 *
 * **A restored stroke is a new row.** Undoing an erase posts the stroke again, and the server
 * gives it a new id; every entry still naming the old id is rewritten to the new one, so the
 * Undo after that one still finds its stroke.
 */

import { useSyncExternalStore } from 'react';

import type { Markup } from './api';

export type SessionEntry =
  | { readonly type: 'drew'; readonly id: string }
  | { readonly type: 'erased'; readonly rows: readonly Markup[] };

interface SessionState {
  /** Every stroke id this tab drew or restored and has not seen erased. */
  readonly drawn: ReadonlySet<string>;
  /** Oldest first, per version id. */
  readonly history: ReadonlyMap<string, readonly SessionEntry[]>;
}

let state: SessionState = { drawn: new Set(), history: new Map() };
const listeners = new Set<() => void>();

function commit(next: SessionState): void {
  state = next;
  for (const listener of listeners) listener();
}

function push(versionId: string, entry: SessionEntry): Map<string, readonly SessionEntry[]> {
  const history = new Map(state.history);
  history.set(versionId, [...(history.get(versionId) ?? []), entry]);
  return history;
}

/** A stroke this person just drew. Called by the create mutation for every row it gets back. */
export function recordDrawn(row: Markup): void {
  commit({
    drawn: new Set(state.drawn).add(row.id),
    history: push(row.version_id, { type: 'drew', id: row.id }),
  });
}

/** Strokes the eraser or Clear took back, as one act. */
export function recordErased(versionId: string, rows: readonly Markup[]): void {
  if (rows.length === 0) return;
  const drawn = new Set(state.drawn);
  for (const row of rows) drawn.delete(row.id);
  commit({ drawn, history: push(versionId, { type: 'erased', rows }) });
}

/** A stroke Undo took back. Not an act of its own: Undo already popped the entry. */
export function forgetDrawn(id: string): void {
  const drawn = new Set(state.drawn);
  drawn.delete(id);
  commit({ ...state, drawn });
}

/**
 * Strokes Undo put back, as the rows the server wrote. `pairs` maps each old id to its new row,
 * and every entry of every version naming an old id now names the new one.
 */
export function recordRestored(pairs: readonly (readonly [string, Markup])[]): void {
  if (pairs.length === 0) return;
  const renamed = new Map<string, Markup>(pairs);
  const drawn = new Set(state.drawn);
  for (const [, row] of pairs) drawn.add(row.id);
  const history = new Map<string, readonly SessionEntry[]>();
  for (const [versionId, entries] of state.history) {
    history.set(
      versionId,
      entries.map((entry): SessionEntry =>
        entry.type === 'drew'
          ? { type: 'drew', id: renamed.get(entry.id)?.id ?? entry.id }
          : { type: 'erased', rows: entry.rows.map((row) => renamed.get(row.id) ?? row) },
      ),
    );
  }
  commit({ drawn, history });
}

/** Take the newest act on this version off the history, and hand it to whoever undoes it. */
export function popEntry(versionId: string): SessionEntry | null {
  const entries = state.history.get(versionId) ?? [];
  const last = entries[entries.length - 1];
  if (last === undefined) return null;
  const history = new Map(state.history);
  history.set(versionId, entries.slice(0, -1));
  commit({ ...state, history });
  return last;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useMarkupSession(): SessionState {
  return useSyncExternalStore(subscribe, () => state);
}
